import { useState, useCallback, useMemo } from "react";
import { BookingStatus, ActivityItem, TripTransport } from "@/types/trip";
import useTripStore from "@/stores/tripBuilder/useTrip";
import { toast } from "@/hooks/use-toast";
import debounce from "lodash/debounce";

type ItemType = "transport" | "activity" | "accommodation";

export const useBookingStatus = () => {
  const [isUpdating, setIsUpdating] = useState<{ [key: string]: boolean }>({});
  const { tripData, updateTripData, updateLocation } = useTripStore();

  // Memoize the update functions for different item types
  const updateFunctions = useMemo(
    () => ({
      transport: (itemId: string, newStatus: BookingStatus) => {
        if (!tripData) return;
        const updatedTransport = tripData.transport.map((t) =>
          t.id === itemId ? { ...t, bookingStatus: newStatus } : t
        );
        updateTripData({ ...tripData, transport: updatedTransport });
      },

      activity: (
        itemId: string,
        newStatus: BookingStatus,
        locationId: string,
        date: string
      ) => {
        if (!tripData) return;
        const location = tripData.locations.find((l) => l.id === locationId);
        if (!location) return;

        const updatedItems = location.itinerary_items.map((item) =>
          item.date === date && item.items.some((i) => i.id === itemId)
            ? {
                ...item,
                items: item.items.map((i) =>
                  i.id === itemId ? { ...i, bookingStatus: newStatus } : i
                ),
              }
            : item
        );

        updateLocation(locationId, { itinerary_items: updatedItems });
      },

      accommodation: (
        itemId: string,
        newStatus: BookingStatus,
        locationId: string
      ) => {
        if (!tripData) return;
        const location = tripData.locations.find((l) => l.id === locationId);
        if (!location?.accommodation?.main) return;

        updateLocation(locationId, {
          accommodation: {
            main: { ...location.accommodation.main, bookingStatus: newStatus },
            temp: location.accommodation.temp || null,
          },
        });
      },
    }),
    [tripData, updateTripData, updateLocation]
  );

  // Create a stable debounced update function
  const debouncedUpdate = useMemo(
    () =>
      debounce(
        async (
          itemType: ItemType,
          itemId: string,
          newStatus: BookingStatus,
          locationId?: string,
          date?: string
        ) => {
          try {
            setIsUpdating((prev) => ({ ...prev, [itemId]: true }));

            if (itemType === "transport") {
              updateFunctions.transport(itemId, newStatus);
            } else if (itemType === "activity" && locationId && date) {
              updateFunctions.activity(itemId, newStatus, locationId, date);
            } else if (itemType === "accommodation" && locationId) {
              updateFunctions.accommodation(itemId, newStatus, locationId);
            }
          } catch (error) {
            console.error("Error updating booking status:", error);
            toast({
              title: "Error",
              description: "Failed to update booking status",
              variant: "destructive",
            });
          } finally {
            setIsUpdating((prev) => ({ ...prev, [itemId]: false }));
          }
        },
        500
      ),
    [updateFunctions]
  );

  const updateBookingStatus = useCallback(
    (
      itemType: ItemType,
      itemId: string,
      newStatus: BookingStatus,
      locationId?: string,
      date?: string
    ) => {
      if (isUpdating[itemId]) return;
      debouncedUpdate(itemType, itemId, newStatus, locationId, date);
    },
    [debouncedUpdate, isUpdating]
  );

  return {
    updateBookingStatus,
    isUpdating,
  };
};
